'use client'

import * as React from 'react'
import { useEffect } from 'react'
import Providers from './providers'
import ClientLayoutSelector from '@/components/Layouts/ClientLayoutSelector'
import PageView from '@/components/KarteEventHandlers/PageView'
import Karte from '@/components/ThirdPartyScripts/Karte'
import GTM from '@/components/ThirdPartyScripts/GTM'

export default function ClientLayout({ children }: { children: React.ReactNode }) {
  // iPhone検出とclass付与
  useEffect(() => {
    if (typeof window !== 'undefined' && /iPhone|iPod/.test(navigator.userAgent)) {
      document.body.classList.add('deviceIphone')
    }
  }, [])

  // 外部スクリプトが期待する関数やオブジェクトをダミーとして定義
  useEffect(() => {
    // SafariBooks拡張機能やサードパーティスクリプトが期待する関数
    if (typeof window !== 'undefined') {
      // removeAllHighlightsForSB が未定義の場合のみダミー関数を定義
      if (typeof (window as any).removeAllHighlightsForSB === 'undefined') {
        ;(window as any).removeAllHighlightsForSB = function () {
          // 何もしないダミー関数
          if (process.env.NODE_ENV === 'development') {
            console.debug('removeAllHighlightsForSB called (dummy function)')
          }
        }
      }

      // iOS WebView用のダミーオブジェクト（先置きは app/layout.tsx の head。ここは hydration 後の再保証）
      if (typeof (window as any).webkit === 'undefined') {
        ;(window as any).webkit = {}
      }
      if (typeof (window as any).webkit?.messageHandlers === 'undefined') {
        ;(window as any).webkit.messageHandlers = {}
      }
    }
  }, [])

  // グローバルエラーハンドラー - 外部スクリプトのエラーを抑制
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const errorMessage = event.message || ''

      // 外部スクリプトからの既知のエラーを抑制
      const suppressedErrors = [
        'window.webkit.messageHandlers',
        'removeAllHighlightsForSB',
        'webkit.messageHandlers',
        // 他の一般的な外部スクリプトエラーも追加可能
      ]

      if (suppressedErrors.some((err) => errorMessage.includes(err))) {
        // エラーを抑制し、コンソールに警告として記録
        if (process.env.NODE_ENV === 'development') {
          console.warn('Suppressed external script error:', errorMessage)
        }
        event.preventDefault()
        return true
      }

      return false
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const errorMessage = event.reason?.message || String(event.reason) || ''

      // 外部スクリプトからの既知のエラーを抑制
      const suppressedErrors = [
        'window.webkit.messageHandlers',
        'removeAllHighlightsForSB',
        'webkit.messageHandlers',
      ]

      if (suppressedErrors.some((err) => errorMessage.includes(err))) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Suppressed external script promise rejection:', errorMessage)
        }
        event.preventDefault()
        return true
      }

      return false
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  return (
    <>
      <GTM />
      <Karte />
      <Providers>
        <PageView />
        <ClientLayoutSelector>{children}</ClientLayoutSelector>
      </Providers>
    </>
  )
}
